// client/src/pages/School_Admin/Academics/TimetableGeneratorPage.js
//
// THE TIMETABLE GENERATOR — secondary only.
//
// Nursery and primary have one class teacher per class, so there is nothing to
// solve there; every class this page lists is first or second cycle, and the
// server refuses anything else.
//
// The tabs are in the order the work actually happens, and the two that work
// today come first: a school cannot generate a week it has not described, and
// it should not generate one on top of a timetable that already contradicts
// itself.
//
// The existing designer at /timetable is untouched. This page never edits a
// single slot — it describes the week, checks whether it can exist, and (once
// the solver lands) proposes a whole one for review.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControl, IconButton, LinearProgress, MenuItem, Paper, Select, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Tab, Tabs, TextField,
  Tooltip, Typography, useTheme,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { fetchJson } from '../../../utils/fetchJson';
import { buildDaySlots, weekDays } from '../../../utils/bellSchedule.generated';
import { TeachersTab, AllocationTab } from './TimetableTeachersTab';
import OptionsTab from './TimetableOptionsTab';
import GenerateTab from './TimetableGenerateTab';
import ExamsTab from './TimetableExamsTab';

const API = '/api/academics/timetables';

/* -------------------------------------------------------------------------- */

const DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The days a config runs over, as "Mon–Fri". */
const daysLabel = (cfg) => {
  const ds = weekDays(cfg?.weekStart ?? 1, cfg?.weekEnd ?? 5);
  if (!ds.length) return '—';
  return ds.length === 1 ? DAY_SHORT[ds[0]] : `${DAY_SHORT[ds[0]]}–${DAY_SHORT[ds[ds.length - 1]]}`;
};

const breakLabel = (b) => (Number(b?.periodsBefore) && Number(b?.durationMinutes)
  ? `after ${b.periodsBefore}, ${b.durationMinutes} min` : 'none');

/**
 * WHAT THESE SETTINGS ACTUALLY PRODUCE, said in the row that sets them.
 *
 * "Consecutive: up to triple" and "Per day: 1, together" sound like they ask for
 * triple periods. They do not: `maxConsecutive` only PERMITS a run that long,
 * and a subject appearing once a day has nothing to join up, so the week comes
 * out as singles. Six of this school's seven "together" subjects were set that
 * way and would have produced singles with no indication why.
 *
 * A rule nobody can predict the effect of is a rule nobody can set correctly, so
 * the effect is printed rather than explained.
 *
 * Mirrors `unitSizes` in server/services/timetable/demand.js — the same
 * arithmetic, and tools/timetable-conformance.mjs runs both over the same
 * settings so they cannot drift.
 */
function runsAs(row) {
  const periods = Math.max(0, Math.floor(Number(row?.periodsPerWeek) || 0));
  if (!periods) return null;
  // Absent means the column default — 2 / 2 / block — exactly as demand.js
  // reads it. tools/timetable-conformance.mjs runs both over the same settings.
  const runCap = Math.max(1, Math.floor(Number(row?.maxConsecutive) || 2));
  const perDay = Math.max(1, Math.floor(Number(row?.occurrencesPerDay) || 2));
  const size = (row?.blockPolicy || 'block') === 'block' ? Math.min(perDay, runCap) : 1;

  if (size <= 1) {
    return {
      text: `${periods} single${periods === 1 ? '' : 's'}`,
      // The case worth warning about: the school asked for adjacency and a run
      // longer than one, and will get neither.
      warn: row?.blockPolicy === 'block' && runCap > 1,
    };
  }
  const whole = Math.floor(periods / size);
  const left = periods % size;
  // THE REMAINDER IS ONE UNIT OF ITS OWN SIZE, not a handful of singles: eight
  // periods in threes is [3, 3, 2], so it is two triples and a DOUBLE.
  // Describing it as "2 singles" would be a preview that contradicts the week.
  const word = (n) => ({ 1: 'single', 2: 'double', 3: 'triple', 4: 'quadruple' }[n] || `run of ${n}`);
  const say = (count, n) => `${count} ${word(n)}${count === 1 ? '' : 's'}`;

  // Fewer periods than the block needs: there is no block, only the remainder.
  if (!whole) return { text: say(1, left), warn: false };

  return {
    text: say(whole, size) + (left ? ` + ${say(1, left)}` : ''),
    warn: false,
  };
}

const sameDay = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** An amber/red/green icon, so a long report is scannable before it is read. */
function LevelIcon({ level }) {
  const theme = useTheme();
  if (level === 'error') return <ErrorOutlineIcon fontSize="small" sx={{ color: theme.palette.error.main }} />;
  if (level === 'warning') return <WarningAmberIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />;
  return <CheckCircleOutlineIcon fontSize="small" sx={{ color: theme.palette.success.main }} />;
}

/* -------------------------------------------------------------------------- */

/**
 * The day as it will actually run, drawn from the values being typed.
 *
 * A break consumes real time, so lengthening it pushes the whole afternoon
 * later — which nobody predicts from the numbers alone. This is the same
 * `buildDaySlots` the solver runs on, generated from core by tools/sync-shared.py,
 * so what is previewed here is what will be scheduled.
 */
function DayPreview({ config }) {
  const theme = useTheme();
  const slots = useMemo(() => buildDaySlots(config), [config]);
  let periodNo = 0;

  if (!slots.length) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        This day holds no periods at all. Check the start, closing time and period length.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {slots.map((s, i) => {
          if (s.type === 'break') {
            return (
              <Chip
                key={i} size="small" variant="outlined"
                label={`Break ${s.start}–${s.end}`}
                sx={{
                  borderStyle: 'dashed',
                  color: theme.palette.text.secondary,
                  bgcolor: theme.palette.action.hover,
                }}
              />
            );
          }
          periodNo += 1;
          return (
            <Chip key={i} size="small" label={`P${periodNo} ${s.start}–${s.end}`} />
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {periodNo} periods a day × {weekDays(config?.weekStart ?? 1, config?.weekEnd ?? 5).length} days
        {' = '}<strong>{periodNo * weekDays(config?.weekStart ?? 1, config?.weekEnd ?? 5).length}</strong>
        {' '}periods a week
      </Typography>
    </Box>
  );
}

/** The eight fields a school day is made of. */
function DayFields({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const setBreak = (key, patch) => set({ [key]: { ...(value[key] || {}), ...patch } });

  const num = (v) => (v === '' ? 0 : Number(v));

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
        <TextField
          size="small" label="Starts" type="time" sx={{ width: 140 }}
          InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }}
          value={value.dayStart || ''} onChange={(e) => set({ dayStart: e.target.value })}
        />
        <TextField
          size="small" label="Closes" type="time" sx={{ width: 140 }}
          InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }}
          value={value.dayEnd || ''} onChange={(e) => set({ dayEnd: e.target.value })}
        />
        <TextField
          size="small" label="Period" type="number" sx={{ width: 130 }}
          InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">min</Typography> }}
          inputProps={{ min: 20, max: 120 }}
          value={value.periodMinutes ?? ''} onChange={(e) => set({ periodMinutes: num(e.target.value) })}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Typography variant="caption" color="text.secondary">Week starts</Typography>
          <Select value={value.weekStart ?? 1} onChange={(e) => set({ weekStart: Number(e.target.value) })}>
            {[1, 2, 3, 4, 5, 6].map((d) => <MenuItem key={d} value={d}>{DAY_NAMES[d]}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Typography variant="caption" color="text.secondary">Week ends</Typography>
          <Select value={value.weekEnd ?? 5} onChange={(e) => set({ weekEnd: Number(e.target.value) })}>
            {[1, 2, 3, 4, 5, 6].map((d) => <MenuItem key={d} value={d}>{DAY_NAMES[d]}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {/* BREAKS ARE PLACED BY COUNT, NOT BY CLOCK — "after 3 periods, 15
          minutes". A school that lengthens its break does not retype the
          afternoon, and a class with shorter periods gets its break at the
          right point in the morning rather than at a fixed hour. */}
      {[['shortBreak', 'Short break'], ['longBreak', 'Long break']].map(([key, label]) => (
        <Stack key={key} direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" sx={{ minWidth: 96 }}>{label}</Typography>
          <TextField
            size="small" label="after period" type="number" sx={{ width: 130 }}
            inputProps={{ min: 0, max: 12 }}
            value={value[key]?.periodsBefore ?? 0}
            onChange={(e) => setBreak(key, { periodsBefore: num(e.target.value) })}
          />
          <TextField
            size="small" label="minutes" type="number" sx={{ width: 120 }}
            inputProps={{ min: 0, max: 120 }}
            value={value[key]?.durationMinutes ?? 0}
            onChange={(e) => setBreak(key, { durationMinutes: num(e.target.value) })}
          />
          <Typography variant="caption" color="text.secondary">
            {Number(value[key]?.periodsBefore) && Number(value[key]?.durationMinutes)
              ? '' : 'zero means no break'}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * Setup · School day.
 *
 * The school sets its day ONCE and overrides only the classes that differ.
 * Without the default, sixteen classes are sixteen chances to mistype 07:30 —
 * and closing times genuinely do differ, which is why the override exists at
 * all.
 */
function SchoolDayTab({ schoolDay, classes, onSave, onReset, busy }) {
  const [draftDefault, setDraftDefault] = useState(schoolDay?.day || {});
  const [editingClass, setEditingClass] = useState(null);   // {id, name, draft}

  useEffect(() => { setDraftDefault(schoolDay?.day || {}); }, [schoolDay]);

  const defaultDirty = !sameDay(draftDefault, schoolDay?.day || {});

  return (
    <Stack spacing={3}>
      {!schoolDay?.configured && (
        <Alert severity="warning">
          This school has never set its day, so every class below is on the built-in
          grid — {schoolDay?.day?.dayStart}–{schoolDay?.day?.dayEnd},{' '}
          {schoolDay?.day?.periodMinutes}-minute periods, {daysLabel(schoolDay?.day)}.
          Until it is set, every "periods allocated" figure on this page is measured
          against a week nobody described.
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>School default</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Every class follows this unless it is given its own day below.
          </Typography>
          <DayFields value={draftDefault} onChange={setDraftDefault} />
          <DayPreview config={draftDefault} />
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="contained" size="small" disabled={!defaultDirty || busy}
              onClick={() => onSave({ scope: 'default', day: draftDefault })}
            >
              Save the school day
            </Button>
            {defaultDirty && (
              <Button size="small" onClick={() => setDraftDefault(schoolDay?.day || {})}>Cancel</Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Box>
        <Typography variant="subtitle1" gutterBottom>Classes that differ</Typography>
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Class</TableCell>
                <TableCell>Day</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Breaks</TableCell>
                <TableCell>Days</TableCell>
                <TableCell align="right">Periods / week</TableCell>
                <TableCell align="right" sx={{ width: 170 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {classes.map((c) => (
                <TableRow key={c.id} hover selected={editingClass?.id === c.id}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{c.name}</Typography>
                      {/* WHERE THIS DAY CAME FROM. Without it an admin cannot
                          tell "we chose this" from "nobody has chosen". */}
                      {c.overridden
                        ? <Chip size="small" color="primary" variant="outlined" label="own day" />
                        : <Chip size="small" variant="outlined" label="default" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{c.day.dayStart}–{c.day.dayEnd}</TableCell>
                  <TableCell>{c.day.periodMinutes} min</TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">short: {breakLabel(c.day.shortBreak)}</Typography>
                    <Typography variant="caption" display="block">long: {breakLabel(c.day.longBreak)}</Typography>
                  </TableCell>
                  <TableCell>{daysLabel(c.day)}</TableCell>
                  <TableCell align="right">{c.weeklySlots}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setEditingClass(
                      editingClass?.id === c.id ? null : { id: c.id, name: c.name, draft: c.day },
                    )}>
                      {editingClass?.id === c.id ? 'Close' : 'Give it its own day'}
                    </Button>
                    {c.overridden && (
                      <Button size="small" color="inherit" disabled={busy}
                        onClick={() => { setEditingClass(null); onReset(c.id); }}>
                        Reset
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!classes.length && (
                <TableRow><TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">No secondary classes.</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {editingClass && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>{editingClass.name}</Typography>
            <DayFields
              value={editingClass.draft}
              onChange={(draft) => setEditingClass({ ...editingClass, draft })}
            />
            <DayPreview config={editingClass.draft} />
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="contained" size="small" disabled={busy}
                onClick={async () => {
                  await onSave({ scope: 'class', classId: editingClass.id, day: editingClass.draft });
                  setEditingClass(null);
                }}
              >
                Save {editingClass.name}'s day
              </Button>
              <Button size="small" onClick={() => setEditingClass(null)}>Cancel</Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */

export default function TimetableGeneratorPage() {
  const { t } = useTranslation();

  const [tab, setTab] = useState('setup');
  const [setupTab, setSetupTab] = useState('day');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [schoolDay, setSchoolDay] = useState(null);
  // WHICH YEAR IS BEING CONFIGURED, and whether it accepts writes at all.
  const [yearInfo, setYearInfo] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [savingDay, setSavingDay] = useState(false);
  const [staff, setStaff] = useState(null);
  const [savingStaff, setSavingStaff] = useState(false);
  const [options, setOptions] = useState(null);
  const [selectedClass, setSelectedClass] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const setup = await fetchJson(`${API}/solver/setup`);
      const cls = Array.isArray(setup?.classes) ? setup.classes : [];
      setClasses(cls);
      setSubjects(Array.isArray(setup?.subjects) ? setup.subjects : []);
      setSchoolDay(setup?.schoolDay || null);
      setYearInfo({
        academicYearId: setup?.academicYearId ?? null,
        academicYearName: setup?.academicYearName ?? null,
        currentAcademicYearId: setup?.currentAcademicYearId ?? null,
        currentAcademicYearName: setup?.currentAcademicYearName ?? null,
        writable: setup?.writable !== false,
      });
      setSelectedClass((held) => (cls.some((c) => String(c.id) === String(held)) ? held : (cls[0]?.id ?? '')));
    } catch (e) {
      setError(e?.message || t('timetableGenerator.errors.load', { defaultValue: 'Could not load the timetable setup.' }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Loaded on demand: both are whole-school scans, and a school that only wants
  // to fill in periods should not pay for them.
  const loadReadiness = useCallback(async () => {
    try { setReadiness(await fetchJson(`${API}/solver/readiness`)); }
    catch (e) { setReadiness({ error: e?.message || 'Could not run the readiness check.' }); }
  }, []);

  const loadConflicts = useCallback(async () => {
    // THE CLASSES THIS TOOL MANAGES. The generator is secondary only, and a
    // nursery class in its Conflicts tab is a fault it cannot act on and the
    // reader cannot place.
    try { setConflicts(await fetchJson(`${API}/conflicts?scope=secondary`)); }
    catch (e) { setConflicts({ error: e?.message || 'Could not scan for conflicts.' }); }
  }, []);

  const loadStaff = useCallback(async () => {
    try { setStaff(await fetchJson(`${API}/solver/teachers`)); }
    catch (e) { setError(e?.message || 'Could not load the teachers.'); }
  }, []);

  const loadOptions = useCallback(async () => {
    try { setOptions(await fetchJson(`${API}/solver/option-blocks`)); }
    catch (e) { setError(e?.message || 'Could not load the option blocks.'); }
  }, []);

  useEffect(() => {
    if (tab === 'readiness' && !readiness) loadReadiness();
    if (tab === 'conflicts' && !conflicts) loadConflicts();
    if (tab === 'setup' && (setupTab === 'teachers' || setupTab === 'allocation') && !staff) loadStaff();
    if (tab === 'setup' && setupTab === 'pairing' && !options) loadOptions();
    // Generate shows each teacher's ceiling beside their derived week, so it
    // needs the same staff payload the Teachers tab does.
    if (tab === 'generate' && !staff) loadStaff();
  }, [tab, setupTab, readiness, conflicts, staff, options,
    loadReadiness, loadConflicts, loadStaff, loadOptions]);

  /* -- saving one class-subject's settings --------------------------------- */

  // A FINISHED YEAR ACCEPTS NOTHING. Declared above every save that reads it:
  // a const used before its declaration is a temporal dead zone crash, not a
  // quiet undefined.
  const readOnly = !!(yearInfo && !yearInfo.writable);

  const patchSubject = async (row, patch) => {
    if (readOnly) return;
    setBusyId(row.id);
    // Optimistic: the grid is a spreadsheet and a round trip per keystroke
    // makes it feel broken. A failure re-loads, so nothing stays wrong silently.
    //
    // SESSION IS A PROPERTY OF THE SUBJECT, not of this class's link to it: a
    // practical is an afternoon subject everywhere it is taught, which is how
    // the server stores it. Patching only the row that was clicked would leave
    // the other classes showing the old value until a refresh, and the admin
    // would set it again from each of them.
    const subjectWide = 'timeOfDay' in patch || 'timeOfDayStrict' in patch;
    setSubjects((prev) => prev.map((s) => {
      if (s.id === row.id) return { ...s, ...patch };
      if (subjectWide && s.subjectId === row.subjectId) {
        const { timeOfDay, timeOfDayStrict } = patch;
        return {
          ...s,
          ...(timeOfDay !== undefined ? { timeOfDay } : {}),
          ...(timeOfDayStrict !== undefined ? { timeOfDayStrict } : {}),
        };
      }
      return s;
    }));
    try {
      await fetchJson(`${API}/solver/class-subject/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      setReadiness(null);   // the numbers it reported are now stale
    } catch (e) {
      setError(e?.message || 'Could not save that setting.');
      load();
    } finally {
      setBusyId(null);
    }
  };

  /* -- saving the school day ------------------------------------------------ */
  //
  // Not optimistic, unlike the subject grid: changing the day changes how many
  // periods every class has, so the page must show the server's answer rather
  // than its own guess at it.
  const saveDay = async (body) => {
    if (readOnly) return;
    setSavingDay(true);
    setError('');
    try {
      await fetchJson(`${API}/solver/day-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      setReadiness(null);
      await load();
    } catch (e) {
      setError(e?.message || 'Could not save the school day.');
    } finally {
      setSavingDay(false);
    }
  };

  /* -- saving teachers ------------------------------------------------------ */
  //
  // Every one of these changes a teacher's ceiling or their load, and both
  // numbers are shown on the same screen, so the server's answer is re-read
  // rather than guessed at.
  const saveStaff = async (fn) => {
    if (readOnly) return;
    setSavingStaff(true);
    setError('');
    try {
      await fn();
      setReadiness(null);
      setStaff(await fetchJson(`${API}/solver/teachers`));
    } catch (e) {
      setError(e?.message || 'Could not save that.');
    } finally {
      setSavingStaff(false);
    }
  };

  // Same shape as saveStaff: a block changes what every class's week costs, so
  // the readiness numbers are dropped and the server is re-read.
  const [savingOptions, setSavingOptions] = useState(false);
  const saveOptions = async (fn) => {
    if (readOnly) return;
    setSavingOptions(true);
    setError('');
    try {
      await fn();
      setReadiness(null);
      setConflicts(null);
      setOptions(await fetchJson(`${API}/solver/option-blocks`));
    } catch (e) {
      setError(e?.message || 'Could not save that block.');
    } finally {
      setSavingOptions(false);
    }
  };

  const put = (path, body) => fetchJson(`${API}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  // The span the availability grid is drawn over: the earliest any class opens
  // to the latest any closes. Painting 07:00 for a school that starts at 07:30
  // is painting a time nothing can be scheduled in.
  const dayBounds = useMemo(() => {
    const starts = classes.map((c) => c.day?.dayStart).filter(Boolean);
    const ends = classes.map((c) => c.day?.dayEnd).filter(Boolean);
    return {
      min: starts.length ? starts.sort()[0] : (schoolDay?.day?.dayStart || '07:00'),
      max: ends.length ? ends.sort()[ends.length - 1] : (schoolDay?.day?.dayEnd || '18:00'),
    };
  }, [classes, schoolDay]);

  const currentClass = useMemo(
    () => classes.find((c) => String(c.id) === String(selectedClass)) || null,
    [classes, selectedClass],
  );

  const classSubjects = useMemo(
    () => subjects.filter((s) => String(s.classId) === String(selectedClass)),
    [subjects, selectedClass],
  );

  // The number a school most needs to see: what it has promised against what the
  // week can hold.
  const promised = useMemo(
    () => classSubjects.reduce((sum, s) => sum + (Number(s.periodsPerWeek) || 0), 0),
    [classSubjects],
  );

  /* -- render --------------------------------------------------------------- */

  if (loading) {
    return <Box sx={{ p: 3 }}><LinearProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <AutoFixHighIcon />
        <Typography variant="h5">
          {t('timetableGenerator.title', { defaultValue: 'Timetable Generator' })}
        </Typography>
        <Chip size="small" label={t('timetableGenerator.secondaryOnly', { defaultValue: 'Secondary only' })} />
        {/* THE YEAR, ALWAYS. Every setting on this page belongs to one, and an
            admin who cannot see which is one switch away from configuring the
            wrong one. */}
        {yearInfo?.academicYearName && (
          <Chip
            size="small"
            color={yearInfo.writable ? 'primary' : 'warning'}
            variant="outlined"
            label={yearInfo.academicYearName + (yearInfo.writable ? '' : ' · read-only')}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={t('common.refresh', { defaultValue: 'Refresh' })}>
          <IconButton onClick={() => { setReadiness(null); setConflicts(null); load(); }}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('timetableGenerator.intro', {
          defaultValue: 'Describe the week, check that it can exist, then generate it. '
            + 'Nursery and primary are not included — there one class teacher takes the whole class.',
        })}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* SAID ONCE, AT THE TOP, AND EVERY SAVE DISABLED WITH IT.
          A finished year is read-only — availability and allocation describe a
          year, and editing a past one rewrites history. But the page used to
          say nothing: an admin painted a teacher's week, pressed save, and the
          refusal landed in a banner above a long page while the grid reset
          underneath them. It read as a save that silently did nothing. */}
      {yearInfo && !yearInfo.writable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('timetableGenerator.readOnlyYear', {
            viewing: yearInfo.academicYearName || `year ${yearInfo.academicYearId}`,
            current: yearInfo.currentAcademicYearName || `year ${yearInfo.currentAcademicYearId}`,
            defaultValue: 'You are viewing {{viewing}}, which has finished and cannot be changed. '
              + 'Switch to {{current}} to set up a timetable. Everything below is shown for '
              + 'reference and nothing here will save.',
          })}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
        <Tab value="setup" label={t('timetableGenerator.tabs.setup', { defaultValue: 'Setup' })} />
        <Tab value="readiness" label={t('timetableGenerator.tabs.readiness', { defaultValue: 'Readiness' })} />
        <Tab value="conflicts" label={t('timetableGenerator.tabs.conflicts', { defaultValue: 'Conflicts' })} />
        <Tab value="generate" label={t('timetableGenerator.tabs.generate', { defaultValue: 'Generate' })} />
        {/* AFTER Generate, because the exam timetable is built from the week the
            classes are taught — there is nothing to sit until that exists. */}
        <Tab value="exams" label={t('timetableGenerator.tabs.exams', { defaultValue: 'Exams' })} />
      </Tabs>

      {/* ---------------------------------------------------------------- Setup */}
      {tab === 'setup' && (
        <>
          <Tabs
            value={setupTab} onChange={(_, v) => setSetupTab(v)}
            variant="scrollable" scrollButtons="auto"
            sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none' } }}
          >
            <Tab value="day" label={t('timetableGenerator.tabs.schoolDay', { defaultValue: 'School day' })} />
            <Tab value="subjects" label={t('timetableGenerator.tabs.subjects', { defaultValue: 'Subjects' })} />
            <Tab value="teachers" label={t('timetableGenerator.tabs.teachers', { defaultValue: 'Teachers' })} />
            <Tab value="allocation" label={t('timetableGenerator.tabs.allocation', { defaultValue: 'Allocation' })} />
            <Tab value="pairing" label={t('timetableGenerator.tabs.pairing', { defaultValue: 'Pairing' })} />
          </Tabs>

          {setupTab === 'day' && (
            <SchoolDayTab
              schoolDay={schoolDay}
              classes={classes}
              busy={savingDay || readOnly}
              onSave={saveDay}
              onReset={(classId) => saveDay({ scope: 'class', classId, reset: true })}
            />
          )}

          {setupTab === 'teachers' && (
            <TeachersTab
              data={staff}
              bounds={dayBounds}
              busy={savingStaff || readOnly}
              onSaveCaps={(who, caps) => saveStaff(
                // AN IDENTITY, NOT A USER ID — `u:93` or `p:4`. Encoded, because
                // the colon is legal in a path segment only by the letter of the
                // spec, and proxies have been known to disagree.
                () => put(`/solver/teacher/${encodeURIComponent(who)}`, caps),
              )}
              onSaveAvailability={(who, windows) => saveStaff(
                () => put(`/solver/teacher/${encodeURIComponent(who)}/availability`, { windows }),
              )}
              onAddPlaceholder={(body) => saveStaff(() => fetchJson(`${API}/solver/placeholder-teacher`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
              }))}
              onDeletePlaceholder={(id) => saveStaff(
                () => fetchJson(`${API}/solver/placeholder-teacher/${id}`, { method: 'DELETE' }),
              )}
            />
          )}

          {setupTab === 'allocation' && (
            <AllocationTab
              data={staff}
              busy={savingStaff || readOnly}
              onSave={(body) => saveStaff(() => put('/solver/assignments', body))}
            />
          )}

          {setupTab === 'pairing' && (
            <OptionsTab
              data={options}
              busy={savingOptions || readOnly}
              onSave={(body) => saveOptions(() => put('/solver/option-block', body))}
              onDelete={(id) => saveOptions(() => fetchJson(`${API}/solver/option-block/${id}`, { method: 'DELETE' }))}
            />
          )}
        </>
      )}

      {tab === 'setup' && setupTab === 'subjects' && (
        <>
          {!classes.length ? (
            <Alert severity="info">
              {t('timetableGenerator.noClasses', {
                defaultValue: 'No secondary classes found. The generator only covers Forms 1–5 and Lower/Upper Sixth.',
              })}
            </Alert>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <Select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
                    {classes.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {/* THE CLASS NAME, AND NOTHING ELSE. The stream and the
                            level were appended to help tell classes apart; a
                            school names its own classes distinctly already, and
                            "UpperSixth Arts · Arts — Lower / Upper Sixth" says
                            the same thing three times. */}
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {currentClass && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" variant="outlined"
                      label={`${currentClass.day.dayStart}–${currentClass.day.dayEnd}`} />
                    <Chip size="small" variant="outlined"
                      label={`${currentClass.day.periodMinutes} min periods`} />
                    <Chip size="small" variant="outlined"
                      label={`${currentClass.periodsPerDay} × ${currentClass.daysPerWeek} days`} />
                    {/* THE NUMBER THAT MATTERS. A school discovers here, before
                        generating anything, that it has promised more periods
                        than the week holds. */}
                    <Chip
                      size="small"
                      color={promised > currentClass.weeklySlots ? 'error' : (promised ? 'success' : 'default')}
                      label={`${promised} of ${currentClass.weeklySlots} periods allocated`}
                    />
                  </Stack>
                )}
              </Stack>

              {currentClass && promised > currentClass.weeklySlots && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t('timetableGenerator.overSubscribed', {
                    cls: currentClass.name, need: promised, have: currentClass.weeklySlots,
                    defaultValue: '{{cls}} needs {{need}} periods but its week holds {{have}}. '
                      + 'No timetable can satisfy this until the periods or the school day change.',
                  })}
                </Alert>
              )}

              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('timetableGenerator.col.subject', { defaultValue: 'Subject' })}</TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>
                        {t('timetableGenerator.col.periods', { defaultValue: 'Periods/week' })}
                      </TableCell>
                      <TableCell sx={{ width: 150 }}>
                        {t('timetableGenerator.col.session', { defaultValue: 'Session' })}
                      </TableCell>
                      <TableCell sx={{ width: 160 }}>
                        <Tooltip title={t('timetableGenerator.col.consecutiveHelp', {
                          defaultValue: 'A safety limit: the most periods of this subject a class '
                            + 'may ever sit back to back. It does not create double periods on its '
                            + 'own — use "How often each day" for that.',
                        })}>
                          <span>{t('timetableGenerator.col.consecutive', { defaultValue: 'Never more than' })}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: 190 }}>
                        <Tooltip title={t('timetableGenerator.col.perDayHelp', {
                          defaultValue: 'How many lessons of this subject a class has in one day, '
                            + 'and whether they sit side by side. Two, side by side, is a double '
                            + 'period.',
                        })}>
                          <span>{t('timetableGenerator.col.perDay', { defaultValue: 'How often each day' })}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: 150 }}>
                        {t('timetableGenerator.col.runsAs', { defaultValue: 'You will get' })}
                      </TableCell>
                      <TableCell sx={{ width: 140 }}>
                        {t('timetableGenerator.col.core', { defaultValue: 'Core for' })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classSubjects.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">{row.subjectName}</Typography>
                            {row.subjectCode && <Chip size="small" label={row.subjectCode} />}
                            {busyId === row.id && <CircularProgress size={12} />}
                          </Stack>
                        </TableCell>

                        <TableCell align="right">
                          <TextField
                            size="small" type="number" variant="standard"
                            inputProps={{ min: 0, max: 40, style: { textAlign: 'right' } }}
                            value={row.periodsPerWeek ?? ''}
                            placeholder="—"
                            onChange={(e) => patchSubject(row, {
                              periodsPerWeek: e.target.value === '' ? null : Number(e.target.value),
                            })}
                          />
                        </TableCell>

                        <TableCell>
                          <Select
                            size="small" variant="standard" fullWidth value={row.timeOfDay || 'any'}
                            onChange={(e) => patchSubject(row, { timeOfDay: e.target.value })}
                          >
                            <MenuItem value="any">{t('timetableGenerator.session.any', { defaultValue: 'Any time' })}</MenuItem>
                            <MenuItem value="morning">{t('timetableGenerator.session.morning', { defaultValue: 'Morning' })}</MenuItem>
                            <MenuItem value="afternoon">{t('timetableGenerator.session.afternoon', { defaultValue: 'Afternoon' })}</MenuItem>
                          </Select>
                          {/* HARD OR SOFT. "Maths in the morning" as an absolute
                              rule can make a week impossible on its own; as a
                              preference it costs nothing. The solver needs to
                              know which was meant, and only the school does. */}
                          {(row.timeOfDay && row.timeOfDay !== 'any') && (
                            <Chip
                              size="small"
                              sx={{ mt: 0.5, height: 20, cursor: 'pointer' }}
                              color={row.timeOfDayStrict ? 'warning' : 'default'}
                              variant={row.timeOfDayStrict ? 'filled' : 'outlined'}
                              label={row.timeOfDayStrict
                                ? t('timetableGenerator.session.strict', { defaultValue: 'must be' })
                                : t('timetableGenerator.session.preferred', { defaultValue: 'prefer' })}
                              onClick={() => patchSubject(row, { timeOfDayStrict: !row.timeOfDayStrict })}
                            />
                          )}
                        </TableCell>

                        <TableCell>
                          <Select
                            size="small" variant="standard" fullWidth value={row.maxConsecutive ?? 2}
                            onChange={(e) => patchSubject(row, { maxConsecutive: Number(e.target.value) })}
                          >
                            <MenuItem value={1}>{t('timetableGenerator.block.single', { defaultValue: '1 period in a row' })}</MenuItem>
                            <MenuItem value={2}>{t('timetableGenerator.block.double', { defaultValue: '2 in a row (a double)' })}</MenuItem>
                            <MenuItem value={3}>{t('timetableGenerator.block.triple', { defaultValue: '3 in a row (a triple)' })}</MenuItem>
                            <MenuItem value={4}>{t('timetableGenerator.block.quad', { defaultValue: '4 in a row' })}</MenuItem>
                          </Select>
                        </TableCell>

                        <TableCell>
                          {/* READS AS A SENTENCE: "2 lessons a day, side by side".
                              The number and the policy are one decision and were
                              two unlabelled controls sitting next to each other. */}
                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            <TextField
                              size="small" type="number" variant="standard"
                              inputProps={{ min: 1, max: 6, style: { width: 30, textAlign: 'right' } }}
                              value={row.occurrencesPerDay ?? 2}
                              onChange={(e) => patchSubject(row, { occurrencesPerDay: Number(e.target.value) })}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {t('timetableGenerator.perDay.lessons', {
                                count: row.occurrencesPerDay ?? 2,
                                defaultValue: 'periods a day,',
                              })}
                            </Typography>
                            <Select
                              size="small" variant="standard" value={row.blockPolicy || 'block'}
                              onChange={(e) => patchSubject(row, { blockPolicy: e.target.value })}
                              sx={{ minWidth: 120 }}
                            >
                              <MenuItem value="either">
                                {t('timetableGenerator.policy.either', { defaultValue: "side by side or apart — doesn't matter" })}
                              </MenuItem>
                              <MenuItem value="block">
                                {t('timetableGenerator.policy.block', { defaultValue: 'side by side (one long lesson)' })}
                              </MenuItem>
                              <MenuItem value="single">
                                {t('timetableGenerator.policy.apart', { defaultValue: 'spread apart in the day' })}
                              </MenuItem>
                            </Select>
                          </Stack>
                        </TableCell>

                        {/* WHAT THOSE TWO SETTINGS ACTUALLY PRODUCE. Printed
                            rather than explained, because a rule nobody can
                            predict the effect of is a rule nobody can set. */}
                        <TableCell>
                          {(() => {
                            const out = runsAs(row);
                            if (!out) {
                              return (
                                <Typography variant="caption" color="text.secondary">
                                  {t('timetableGenerator.runsAs.noPeriods', { defaultValue: 'set periods first' })}
                                </Typography>
                              );
                            }
                            return (
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography variant="caption" color={out.warn ? 'warning.main' : 'text.secondary'}>
                                  {out.text}
                                </Typography>
                                {out.warn && (
                                  <Tooltip title={t('timetableGenerator.runsAs.warnHelp', {
                                    defaultValue: 'You asked for the lessons side by side, but there is '
                                      + 'only one a day — so there is nothing to put beside anything. '
                                      + 'Raise "How often each day" to 2 for a double period.',
                                  })}>
                                    <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                  </Tooltip>
                                )}
                              </Stack>
                            );
                          })()}
                        </TableCell>

                        <TableCell>
                          <Select
                            size="small" variant="standard" fullWidth value={row.coreForStream || ''}
                            displayEmpty
                            onChange={(e) => patchSubject(row, { coreForStream: e.target.value || null })}
                          >
                            <MenuItem value="">—</MenuItem>
                            <MenuItem value="arts">{t('timetableGenerator.core.arts', { defaultValue: 'Arts' })}</MenuItem>
                            <MenuItem value="science">{t('timetableGenerator.core.science', { defaultValue: 'Science' })}</MenuItem>
                            <MenuItem value="both">{t('timetableGenerator.core.both', { defaultValue: 'Both' })}</MenuItem>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!classSubjects.length && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            {t('timetableGenerator.noSubjects', {
                              defaultValue: 'This class has no subjects allocated yet. Add them in Class Assignments first.',
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('timetableGenerator.setupHint', {
                  defaultValue: '"Core for" marks a subject every student of that stream takes. '
                    + 'Two subjects core for the same stream can never share a slot; two core for '
                    + 'different streams can, which is what makes a Sixth Form week fit.',
                })}
              </Typography>
            </>
          )}
        </>
      )}

      {/* ------------------------------------------------------------ Readiness */}
      {tab === 'readiness' && (
        <>
          {!readiness ? <LinearProgress /> : readiness.error ? (
            <Alert severity="error">{readiness.error}</Alert>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Chip color={readiness.counts?.errors ? 'error' : 'default'}
                  label={`${readiness.counts?.errors || 0} blocking`} />
                <Chip color={readiness.counts?.warnings ? 'warning' : 'default'}
                  label={`${readiness.counts?.warnings || 0} to check`} />
                <Chip color="success" variant="outlined" label={`${readiness.counts?.ok || 0} ready`} />
                <Button size="small" onClick={() => { setReadiness(null); loadReadiness(); }}>
                  {t('common.refresh', { defaultValue: 'Refresh' })}
                </Button>
              </Stack>

              <Paper variant="outlined">
                <Stack divider={<Divider />}>
                  {(readiness.findings || []).map((f, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 1.5 }}>
                      <LevelIcon level={f.level} />
                      <Typography variant="body2"
                        sx={{ color: f.level === 'ok' ? 'text.secondary' : 'text.primary' }}>
                        {f.message}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              {/* WHAT CANNOT YET BE CHECKED, said plainly. A readiness report
                  that stays silent about the checks it cannot run reads as a
                  clean bill of health. */}
              {!!(readiness.pending || []).length && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t('timetableGenerator.notYetChecked', { defaultValue: 'Not yet checked' })}
                  </Typography>
                  {readiness.pending.map((p, i) => (
                    <Typography key={i} variant="body2">• {p}</Typography>
                  ))}
                </Alert>
              )}
            </>
          )}
        </>
      )}

      {/* ------------------------------------------------------------ Conflicts */}
      {tab === 'conflicts' && (
        <>
          {!conflicts ? <LinearProgress /> : conflicts.error ? (
            <Alert severity="error">{conflicts.error}</Alert>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip color={conflicts.total ? 'error' : 'success'}
                  label={`${conflicts.total || 0} conflicts in ${conflicts.scanned || 0} slots`} />
                {Object.entries(conflicts.counts || {}).map(([k, v]) => (
                  <Chip key={k} size="small" variant="outlined" label={`${k.replace(/-/g, ' ')}: ${v}`} />
                ))}
                <Button size="small" onClick={() => { setConflicts(null); loadConflicts(); }}>
                  {t('common.refresh', { defaultValue: 'Refresh' })}
                </Button>
              </Stack>

              {!conflicts.total ? (
                <Alert severity="success">
                  {t('timetableGenerator.noConflicts', {
                    defaultValue: 'No teacher is in two places at once, and no class has two lessons at the same time.',
                  })}
                </Alert>
              ) : (
                <Paper variant="outlined">
                  <Stack divider={<Divider />}>
                    {conflicts.conflicts.map((c, i) => (
                      <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 1.5 }}>
                        <LevelIcon level="error" />
                        <Typography variant="body2">{c.message}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('timetableGenerator.conflictsHint', {
                  defaultValue: 'This reads the timetable as it stands today, however it was built. '
                    + 'Saving a class timetable now refuses a teacher clash unless it is explicitly overridden.',
                })}
              </Typography>
            </>
          )}
        </>
      )}

      {/* ---------------------------------------------------------------- Exams */}
      {tab === 'exams' && <ExamsTab readOnly={readOnly} />}

      {/* ------------------------------------------------------------- Generate */}
      {tab === 'generate' && (
        <GenerateTab
          teachers={staff?.teachers || []}
          onApplied={() => { setConflicts(null); setReadiness(null); }}
        />
      )}

    </Box>
  );
}
